import React, { useState } from 'react';
import { useAccountExecutives, execId } from '../../hooks/useAccountExecutives';

/**
 * Edit the account-executive roster behind the configurator's dropdown
 * (CONF-177). adminAccess-gated in adminRoutes.jsx; the model's @auth is the
 * real enforcement, this page is the affordance.
 *
 * The page manages `allExecutives`, not `executives` — an admin has to be able
 * to see and delete a test-only AE even from a production build, where the
 * dropdown itself hides those rows.
 */

// Deliberately permissive: the roster has held partner and contractor
// addresses, so anything with a local part, an @, and a dotted domain passes.
// The identity check that actually matters (uniqueness) is the id, not this.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BLANK = { name: '', email: '', isTest: false };

export function validateExec({ name, email }, roster, currentId = null) {
  if (!name?.trim()) return 'Name is required.';
  if (!email?.trim()) return 'Email is required.';
  if (!EMAIL_SHAPE.test(email.trim())) return 'That does not look like an email address.';
  // Client-side duplicate check. The deterministic id makes the server the real
  // arbiter — this only turns a confusing GraphQL rejection into a sentence.
  const id = execId(email);
  if (id !== currentId && roster.some((e) => e.id === id)) {
    return 'An account executive with that email already exists.';
  }
  return '';
}

export function AccountExecsPage({ user }) {
  const { allExecutives, status, busy, add, update, remove, seed, refresh } =
    useAccountExecutives({ includeTest: true });

  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(BLANK);
  const [formError, setFormError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const actor = user?.username ?? null;
  // 'fallback' means these rows came from the bundled JSON, not the API. Writing
  // against them would look like it worked and change nothing the fleet sees.
  const canEdit = status === 'ready';

  const run = async (fn, successNotice) => {
    setActionError('');
    setNotice('');
    try {
      const out = await fn();
      if (successNotice) setNotice(successNotice(out));
      return true;
    } catch (e) {
      console.error('Account executive write failed:', e);
      setActionError(e?.errors?.[0]?.message || e?.message || 'That change did not save. Try again.');
      return false;
    }
  };

  const onAdd = async (e) => {
    e.preventDefault();
    const problem = validateExec(draft, allExecutives);
    setFormError(problem);
    if (problem) return;
    const ok = await run(() => add(draft, actor), () => `Added ${draft.name.trim()}.`);
    if (ok) setDraft(BLANK);
  };

  const startEdit = (exec) => {
    setEditingId(exec.id);
    setEdit({ name: exec.name, email: exec.email, isTest: exec.isTest === true });
    setFormError('');
  };

  const onSaveEdit = async (exec) => {
    const problem = validateExec(edit, allExecutives, exec.id);
    setFormError(problem);
    if (problem) return;
    const ok = await run(() => update(exec, edit, actor), () => `Updated ${edit.name.trim()}.`);
    if (ok) setEditingId(null);
  };

  const onRemove = async (exec) => {
    // Hard delete, per CONF-177: the row is gone, so a saved quote that named
    // this AE will show an empty dropdown until a rep re-picks one. Spelled out
    // in the prompt because it is not recoverable from this page.
    const warning =
      `Remove ${exec.name} from the dropdown?\n\n` +
      'This deletes the record. Saved quotes that already name them keep the ' +
      'name on the PDF, but re-opening one in the configurator will show an ' +
      'empty Account Executive field until a rep picks someone.';
    if (!window.confirm(warning)) return;
    await run(() => remove(exec), () => `Removed ${exec.name}.`);
  };

  const onSeed = async () => {
    if (!window.confirm('Copy the built-in list into the editable roster? Existing entries are left alone.')) return;
    await run(() => seed(actor), (r) => `Seeded ${r.created} account executive(s); ${r.skipped} already present.`);
  };

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-xl font-bold text-orange-400">Account Executives</h1>
        <button
          type="button"
          data-testid="aes-refresh"
          onClick={refresh}
          className="text-xs text-gray-400 hover:text-white underline"
        >
          Refresh
        </button>
      </div>
      <p className="text-gray-400 mb-4">
        The list reps choose from in the configurator&apos;s Account Executive field. Changes take
        effect the next time a rep loads the configurator.
      </p>

      {status === 'loading' && <div className="p-4">Loading account executives…</div>}

      {status === 'fallback' && (
        <div
          data-testid="aes-fallback-banner"
          className="mb-4 p-3 rounded border border-orange-400/50 bg-orange-400/10 text-sm"
        >
          <div className="font-bold text-orange-300 mb-1">Showing the built-in list — not yet editable</div>
          <p className="text-gray-300 mb-2">
            The roster is empty or could not be reached, so reps are currently seeing the list
            bundled into the app. Copy it into the database to start editing it.
          </p>
          <button
            type="button"
            data-testid="aes-seed"
            disabled={busy}
            onClick={onSeed}
            className="bg-orange-600 disabled:opacity-50 px-3 py-1 rounded"
          >
            Seed from built-in list
          </button>
        </div>
      )}

      {notice && <div data-testid="aes-notice" className="mb-3 text-green-400 text-sm">{notice}</div>}
      {actionError && <div data-testid="aes-error" className="mb-3 text-red-400 text-sm">{actionError}</div>}

      {status !== 'loading' && (
        <>
          {canEdit && (
            <form onSubmit={onAdd} className="mb-5 flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="ae-new-name">Name</label>
                <input
                  id="ae-new-name"
                  data-testid="aes-new-name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-56"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="ae-new-email">Email</label>
                <input
                  id="ae-new-email"
                  data-testid="aes-new-email"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder="jdoe@vdura.com"
                  className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-64"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300 pb-1" htmlFor="ae-new-istest">
                <input
                  id="ae-new-istest"
                  data-testid="aes-new-istest"
                  type="checkbox"
                  checked={draft.isTest}
                  onChange={(e) => setDraft({ ...draft, isTest: e.target.checked })}
                />
                Test only
              </label>
              <button
                type="submit"
                data-testid="aes-add"
                disabled={busy}
                className="bg-orange-600 disabled:opacity-50 px-3 py-1 rounded"
              >
                Add
              </button>
              {formError && <span data-testid="aes-form-error" className="text-red-400 text-sm pb-1">{formError}</span>}
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-orange-400 text-left">
                  <th className="p-2">name</th>
                  <th className="p-2">email</th>
                  <th className="p-2">visibility</th>
                  <th className="p-2">added by</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {allExecutives.map((exec) => (
                  <tr key={exec.id} className="border-t border-gray-800 align-middle">
                    {editingId === exec.id ? (
                      <>
                        <td className="p-2">
                          <input
                            data-testid="aes-edit-name"
                            value={edit.name}
                            onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                            className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-full"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            data-testid="aes-edit-email"
                            value={edit.email}
                            onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                            className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-full"
                          />
                        </td>
                        <td className="p-2">
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              data-testid="aes-edit-istest"
                              type="checkbox"
                              checked={edit.isTest}
                              onChange={(e) => setEdit({ ...edit, isTest: e.target.checked })}
                            />
                            Test only
                          </label>
                        </td>
                        <td className="p-2 text-gray-500">{exec.createdBy ?? '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            data-testid="aes-save"
                            disabled={busy}
                            onClick={() => onSaveEdit(exec)}
                            className="underline mr-3 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingId(null); setFormError(''); }}
                            className="underline text-gray-400"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-2">{exec.name}</td>
                        <td className="p-2 text-gray-300">{exec.email}</td>
                        <td className="p-2">
                          {exec.isTest === true ? (
                            <span
                              title="A placeholder name. Offered to admins in every build, and to everyone in a dev build — never to reps in production."
                              className="px-1.5 py-0.5 rounded border border-gray-600 text-gray-400 text-xs"
                            >
                              admins &amp; dev builds
                            </span>
                          ) : (
                            <span className="text-gray-500">everyone</span>
                          )}
                        </td>
                        <td className="p-2 text-gray-500">{exec.createdBy ?? '—'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {canEdit && (
                            <>
                              <button
                                type="button"
                                onClick={() => startEdit(exec)}
                                className="underline mr-3"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onRemove(exec)}
                                className="underline text-red-400 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-600 mt-4">
            {allExecutives.length} account executive(s).
            {' '}Editing an email re-creates the entry under the new address.
          </p>
        </>
      )}
    </div>
  );
}

export default AccountExecsPage;
